// ─── CATALOGUE EXPORT ─────────────────────────────────────────────────────────
// Everything the admin "extract the catalogue" buttons need, in one place: the
// column set, the row ordering, and the two writers (CSV and .xlsx). Products.jsx
// only decides WHEN to export; WHAT an export contains is decided here, so the
// spreadsheet and the CSV can never drift apart.
//
// WHY BOTH FORMATS
// The CSV is the round-trip format: its headers are exactly the ones the bulk
// importer reads, so an admin can export, edit in a sheet, and import straight
// back. That machine-readable contract is also its ceiling — CSV has no column
// widths, no number types (Excel happily reads a price as text and refuses to
// SUM it), no RTL for the Arabic columns, and no link on an image URL. The
// .xlsx is the reading copy: same rows, but typed, styled, filterable and
// readable on a phone. Neither replaces the other, so both buttons stay.
//
// WHY EXCELJS IS LOADED ON DEMAND
// Same reasoning as orderWorkbook.js: the browser cannot write .xlsx on its own
// and ExcelJS's browser bundle is ~900 KB. A dynamic import keeps it out of the
// admin's first paint and fetches it on the first click.

import { saveBlob, XLSX_MIME } from './download.js';

const FOREST = 'FF33402B';   // --sedra-forest
const OLIVE  = 'FF5F6B4A';   // --sedra-olive
const WASH   = 'FFEDEFE5';   // --sedra-olive-wash, the zebra band
const RULE   = 'FFD8D4C4';
const MUTED  = 'FF6B6B5E';
const DANGER = 'FF9B2C2C';
const FONT   = 'Arial';      // ships everywhere and shapes Arabic

// ─── "SH" = Sedra Home ────────────────────────────────────────────────────────
// A first-party product is one no vendor owns. Admin rows come straight from
// the products table (vendor_id); catalogue rows come from the RPC, which
// returns vendor_name_* instead. Check both so one predicate serves both.
export const isOwnProduct = (p) =>
  !p?.vendor_id && !p?.vendor_name_en && !p?.vendor_name_ar;

export const SEDRA_HOME = { en: 'Sedra Home', ar: 'سدرة هوم' };

// ─── ordering ─────────────────────────────────────────────────────────────────
/**
 * The one definition of catalogue order, shared by the admin list and by both
 * exports — so an extract is always in the order the admin is looking at.
 *
 * `shFirst` adds a tier ON TOP of the chosen sort rather than replacing it:
 * Sedra Home products float up, and within each group the newest/A–Z order
 * still applies.
 *
 * Returns a new array; the caller's list (often a React Query cache entry) is
 * never mutated.
 */
export function sortCatalogue(list, { shFirst = false, sortBy = 'newest' } = {}) {
  return [...(list || [])].sort((a, b) => {
    if (shFirst) {
      const aOwn = isOwnProduct(a), bOwn = isOwnProduct(b);
      if (aOwn !== bOwn) return aOwn ? -1 : 1;
    }
    if (sortBy === 'newest') return (b.created_at || '') > (a.created_at || '') ? 1 : -1;
    const ca = a.category || 'zzz', cb = b.category || 'zzz';
    if (ca !== cb) return ca.localeCompare(cb);
    return (a.name_en || '').localeCompare(b.name_en || '');
  });
}

// ─── columns ──────────────────────────────────────────────────────────────────
// `field` doubles as the CSV header, so these names are the importer's contract
// — renaming one breaks export → edit → re-import. `inCsv: false` marks a column
// that only makes sense in the reading copy: the importer would ignore it, and a
// column an admin can edit but not import back is a trap.
const num = (v) => (v === '' || v == null || isNaN(Number(v)) ? null : Number(v));

export const CATALOGUE_COLUMNS = [
  { field: 'name_en', width: 42,
    label: { en: 'Product (English)', ar: 'المنتج (إنجليزي)' },
    value: p => p.name_en || '' },

  { field: 'name_ar', width: 42, rtl: true,
    label: { en: 'Product (Arabic)', ar: 'المنتج (عربي)' },
    value: p => p.name_ar || '' },

  { field: 'sku', width: 16,
    label: { en: 'SKU', ar: 'الكود' },
    value: p => p.sku || '' },

  // Reading copy only: the importer has no supplier column, and a vendor is
  // reassigned in the Vendors screen, never by editing a spreadsheet cell.
  { field: 'supplier', width: 22, inCsv: false,
    label: { en: 'Supplier', ar: 'المورّد' },
    value: (p, ctx) => (isOwnProduct(p)
      ? SEDRA_HOME[ctx.lang] || SEDRA_HOME.en
      : ctx.vendorName(p.vendor_id)) },

  { field: 'category', width: 18,
    label: { en: 'Category', ar: 'الفئة' },
    value: p => p.category || '' },

  { field: 'category_ar', width: 18, rtl: true,
    label: { en: 'Category (Arabic)', ar: 'الفئة (عربي)' },
    value: p => p.category_ar || '' },

  { field: 'price', width: 13, align: 'right', fmt: '#,##0.##',
    label: { en: 'Price (EGP)', ar: 'السعر (ج.م)' },
    value: p => num(p.price),
    csv:   p => p.price || '' },

  { field: 'min_qty', width: 10, align: 'center', fmt: '#,##0',
    label: { en: 'Min Qty', ar: 'أقل كمية' },
    value: p => num(p.min_qty) || 1 },

  { field: 'carton_qty', width: 11, align: 'center', fmt: '#,##0',
    label: { en: 'Carton Qty', ar: 'كمية الكرتونة' },
    value: p => num(p.carton_qty) || 1 },

  { field: 'stock', width: 10, align: 'center', fmt: '#,##0',
    label: { en: 'Stock', ar: 'المخزون' },
    value: p => num(p.stock) ?? 0 },

  { field: 'image_url', width: 34, link: true,
    label: { en: 'Cover Image', ar: 'رابط الصورة' },
    value: p => p.image || '' },

  { field: 'images', width: 34,
    label: { en: 'Extra Images', ar: 'صور إضافية' },
    value: p => (Array.isArray(p.images) ? p.images.join('|') : (p.images || '')) },

  { field: 'description', width: 52,
    label: { en: 'Description', ar: 'الوصف' },
    // Newlines would break a CSV row and balloon a spreadsheet row's height.
    value: p => (p.description || '').replace(/\s*\n\s*/g, ' ') },

  { field: 'is_active', width: 11, align: 'center',
    label: { en: 'Status', ar: 'الحالة' },
    value: (p, ctx) => (p.is_active !== false
      ? (ctx.lang === 'ar' ? 'نشط' : 'Active')
      : (ctx.lang === 'ar' ? 'غير نشط' : 'Inactive')),
    // The importer reads a boolean, not a label.
    csv: p => (p.is_active !== false ? 'true' : 'false') },
];

// Resolve the caller's context once, so every column can assume it is complete.
function makeCtx({ lang = 'en', vendors = [] } = {}) {
  const byId = new Map((vendors || []).map(v => [v.id, v]));
  return {
    lang,
    vendorName: (id) => {
      const v = byId.get(id);
      if (!v) return lang === 'ar' ? 'بائع' : 'Vendor';
      return (lang === 'ar' && v.business_name_ar) ? v.business_name_ar : (v.business_name_en || '—');
    },
  };
}

const cellValue = (col, p, ctx) => col.value(p, ctx);
const csvValue  = (col, p, ctx) =>
  (col.csv ? col.csv(p, ctx) : cellValue(col, p, ctx));

const stamp = () => new Date().toISOString().slice(0, 10);

// ─── CSV ──────────────────────────────────────────────────────────────────────
export function catalogueCsv(products, opts = {}) {
  const ctx  = makeCtx(opts);
  const cols = CATALOGUE_COLUMNS.filter(c => c.inCsv !== false);
  const rows = [
    cols.map(c => c.field),
    ...(products || []).map(p => cols.map(c => csvValue(c, p, ctx))),
  ];
  return rows
    .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
}

export function downloadCatalogueCsv(products, opts = {}) {
  // A BOM, or Excel opens the Arabic columns as mojibake. Written through a
  // Blob rather than a data: URI — a 1,000-product catalogue percent-encodes
  // well past the URL length ceiling some browsers enforce.
  const blob = new Blob(['﻿' + catalogueCsv(products, opts)],
                        { type: 'text/csv;charset=utf-8' });
  saveBlob(blob, `sedra_products_${stamp()}.csv`);
  return (products || []).length;
}

// ─── XLSX ─────────────────────────────────────────────────────────────────────
// Excel has no "reset" — every styled cell needs the full spec, so centralise it.
const paint = (cell, { bold = false, size = 10, color = 'FF000000', fill,
                       align = 'left', rtl = false, fmt: numFmt } = {}) => {
  cell.font = { name: FONT, bold, size, color: { argb: color } };
  cell.alignment = { horizontal: align, vertical: 'middle',
                     ...(rtl ? { readingOrder: 'rtl' } : {}) };
  if (fill)   cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
  if (numFmt) cell.numFmt = numFmt;
  return cell;
};

/**
 * Build and download the whole catalogue as a styled .xlsx.
 *
 * @param products  rows in the order they should appear (see sortCatalogue)
 * @param opts.lang     'ar' | 'en' — header language
 * @param opts.vendors  vendor rows, to name the supplier of each vendor product
 * @param opts.shFirst  whether the rows were sorted Sedra-Home-first; recorded
 *                      in the header so the file explains its own order
 */
export async function downloadCatalogueWorkbook(products, opts = {}) {
  const list = products || [];
  if (!list.length) return 0;

  const { default: ExcelJS } = await import('exceljs');
  const ctx  = makeCtx(opts);
  const isAr = ctx.lang === 'ar';
  const cols = CATALOGUE_COLUMNS;
  const LAST = cols.length;

  const wb = new ExcelJS.Workbook();
  wb.creator = 'Sedra Home';
  wb.created = new Date();
  const ws = wb.addWorksheet(isAr ? 'الكتالوج' : 'Catalogue', {
    views: [{ showGridLines: false }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  ws.columns = cols.map(c => ({ key: c.field, width: c.width }));

  // ── title + meta ───────────────────────────────────────────────────────────
  ws.mergeCells(1, 1, 1, LAST);
  paint(ws.getCell('A1'), { bold: true, size: 15, color: 'FFFFFFFF', fill: FOREST, align: 'center' })
    .value = 'Sedra Home  —  PRODUCT CATALOGUE   |   سدرة هوم — كتالوج المنتجات';
  ws.getRow(1).height = 30;

  const active   = list.filter(p => p.is_active !== false).length;
  const own      = list.filter(isOwnProduct).length;
  const dateStr  = new Date().toLocaleDateString(isAr ? 'ar-EG' : 'en-GB',
                     { year: 'numeric', month: 'long', day: 'numeric' });
  const meta = [
    [isAr ? 'تاريخ التصدير' : 'Exported',  dateStr],
    [isAr ? 'عدد المنتجات'  : 'Products',
     isAr ? `${list.length} (${active} نشط · ${list.length - active} غير نشط)`
          : `${list.length}  (${active} active · ${list.length - active} inactive)`],
    [isAr ? 'حسب المورّد' : 'Supplier split',
     isAr ? `${own} سدرة هوم · ${list.length - own} من البائعين`
          : `${own} Sedra Home · ${list.length - own} vendor`],
    [isAr ? 'الترتيب' : 'Order',
     opts.shFirst
       ? (isAr ? 'منتجات سدرة هوم أولاً' : 'Sedra Home products first')
       : (isAr ? 'الترتيب الافتراضي'     : 'Default catalogue order')],
  ];
  meta.forEach(([k, v], i) => {
    const r = 2 + i;
    paint(ws.getCell(r, 1), { bold: true, size: 9, color: OLIVE }).value = k;
    ws.mergeCells(r, 2, r, LAST);
    paint(ws.getCell(r, 2), { size: 10 }).value = v;
  });

  // ── header row ─────────────────────────────────────────────────────────────
  const HEAD = 2 + meta.length + 1;
  cols.forEach((c, i) => {
    paint(ws.getCell(HEAD, i + 1), {
      bold: true, size: 10, color: 'FFFFFFFF', fill: OLIVE,
      align: 'center', rtl: !!c.rtl,
    }).value = c.label[ctx.lang] || c.label.en;
  });
  ws.getRow(HEAD).height = 26;

  // ── rows ───────────────────────────────────────────────────────────────────
  const FIRST = HEAD + 1;
  list.forEach((p, n) => {
    const r = FIRST + n;
    const band = n % 2 ? WASH : undefined;
    const inactive = p.is_active === false;
    cols.forEach((c, i) => {
      const cell = ws.getCell(r, i + 1);
      const value = cellValue(c, p, ctx);
      paint(cell, {
        size: 9, fill: band, rtl: !!c.rtl, fmt: c.fmt,
        align: c.align || (c.rtl ? 'right' : 'left'),
        color: (c.field === 'is_active' && inactive) ? DANGER : 'FF000000',
        bold:  (c.field === 'is_active' && inactive),
      });
      // A clickable image URL — the whole point of opening the extract in Excel
      // rather than a text editor is being able to check a photo in one click.
      cell.value = (c.link && /^https?:\/\//i.test(value))
        ? { text: value, hyperlink: value }
        : value;
      cell.border = { bottom: { style: 'thin', color: { argb: RULE } } };
    });
  });
  const END = FIRST + list.length - 1;

  // ── footnote ───────────────────────────────────────────────────────────────
  const note = END + 2;
  ws.mergeCells(note, 1, note, LAST);
  paint(ws.getCell(note, 1), { size: 9, color: MUTED, align: 'center' }).value = isAr
    ? 'نسخة للقراءة. لتعديل المنتجات بالجملة، صدّر بصيغة CSV وأعد استيرادها.'
    : 'Reading copy. To edit products in bulk, export as CSV and import it back.';

  ws.views = [{ state: 'frozen', ySplit: HEAD, showGridLines: false }];
  ws.autoFilter = { from: { row: HEAD, column: 1 }, to: { row: END, column: LAST } };
  ws.pageSetup.printTitlesRow = `${HEAD}:${HEAD}`;

  const buf = await wb.xlsx.writeBuffer();
  saveBlob(new Blob([buf], { type: XLSX_MIME }), `sedra_products_${stamp()}.xlsx`);
  return list.length;
}
